import React, { useEffect, useMemo, useState } from "react";
import "./BalanceComparativa.css";

export default function BalanceComparativa({
  data = [],
  principalYear,
}) {
  const normalizeText = (value) => {
    return String(value ?? "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toLowerCase();
  };

  const normalizeCode = (value) => {
    return normalizeText(value)
      .replace(/[_\s]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  const safeKey = (value) => {
    return normalizeText(value)
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
  };

  const isPsychologyCode = (value) => {
    const code = normalizeCode(value);

    return [
      "psicologia",
      "psicologa",
      "psicologo",
      "psicologia-g",
      "psicologa-g",
      "psicologo-g",
      "psicologia-m",
      "psicologa-m",
      "psicologo-m",
    ].includes(code);
  };

const getCanonicalSpecialty = (value) => {
  const code = normalizeCode(value);

  if (isPsychologyCode(value)) {
    return {
      key: "psicologia",
      name: "Psicología",
    };
  }

  // Unifica logopeda, logopedia y cualquier variante
  if (
    code === "logopeda" ||
    code === "logopedia" ||
    code.includes("logoped")
  ) {
    return {
      key: "logopedia",
      name: "Logopedia",
    };
  }

  const cleanName =
    String(value ?? "").trim() || "Sin especialidad";

  return {
    key: safeKey(cleanName) || "sin_especialidad",
    name: cleanName,
  };
};

  const num = (value) => {
    if (
      value === null ||
      value === undefined ||
      value === ""
    ) {
      return 0;
    }

    const cleanValue = String(value)
      .replace("%", "")
      .replace(",", ".")
      .trim();

    const parsed = Number(cleanValue);

    return Number.isFinite(parsed) ? parsed : 0;
  };

  const getSpecialtyCode = (item) => {
    return (
      item?.specialty_code ||
      item?.specialty ||
      item?.specialty_name ||
      item?.especialidad ||
      item?.especialidad_codigo ||
      ""
    );
  };

  const getFactorCentro = (specialtyRaw) => {
    const specialty = normalizeCode(specialtyRaw);

    if (
      specialty === "logopeda" ||
      specialty === "logopedia" ||
      specialty === "nutricion" ||
      specialty.includes("logoped") ||
      specialty.includes("nutric")
    ) {
      return 0.3;
    }

    if (
      specialty === "psicologia-g" ||
      specialty === "psicologa-g" ||
      specialty === "psicologo-g" ||
      specialty === "psicologia-m" ||
      specialty === "psicologa-m" ||
      specialty === "psicologo-m"
    ) {
      return 0.4;
    }

    if (
      specialty === "psicologia" ||
      specialty === "psicologa" ||
      specialty === "psicologo"
    ) {
      return 1;
    }

    return 1;
  };

  const getPaymentAmount = (payment) => {
    return num(
      payment?.amount ??
        payment?.importe ??
        payment?.total ??
        payment?.precio ??
        payment?.tarifa ??
        0
    );
  };

  const calcularIngresosReales = (row) => {
    const pagos = Array.isArray(row?.pagos)
      ? row.pagos
      : Array.isArray(row?.payments)
        ? row.payments
        : Array.isArray(row?.pagos_detalle)
          ? row.pagos_detalle
          : Array.isArray(row?.payment_details)
            ? row.payment_details
            : [];

    if (pagos.length > 0) {
      return pagos.reduce((total, pago) => {
        const importe = getPaymentAmount(pago);
        const especialidad = getSpecialtyCode(pago);
        const factorCentro = getFactorCentro(especialidad);

        return total + importe * factorCentro;
      }, 0);
    }

    return num(
      row?.ingresos_reales ??
        row?.ingreso_real ??
        row?.ingresosReal ??
        row?.real_centro ??
        row?.total_real_centro ??
        row?.cobrado_real ??
        0
    );
  };

  const BASE_METRICAS = useMemo(
    () => [
      {
        key: "ingresos",
        label: "Ingresos facturados",
        unit: "EUR",
        level: 0,
        type: "base",
        group: "economia",
      },
      {
        key: "ingresos_reales",
        label: "Ingresos reales",
        unit: "EUR",
        level: 0,
        type: "base",
        group: "economia",
      },
      {
        key: "gastos",
        label: "Gastos",
        unit: "EUR",
        level: 0,
        type: "base",
        group: "economia",
      },
      {
        key: "beneficio",
        label: "Beneficio",
        unit: "EUR",
        level: 0,
        type: "base",
        group: "economia",
      },
      {
        key: "clientes",
        label: "Clientes creados",
        unit: "N",
        level: 0,
        type: "base",
        group: "clientes",
      },
      {
        key: "sesiones",
        label: "Total del centro",
        unit: "N",
        level: 0,
        type: "base",
        group: "sesiones",
      },
      {
        key: "cancelaciones",
        label: "Total del centro",
        unit: "N",
        level: 0,
        type: "base",
        group: "cancelaciones",
      },
      {
        key: "ocupacion",
        label: "Total del centro",
        unit: "PCT",
        level: 0,
        type: "base",
        group: "ocupacion",
      },
    ],
    []
  );

  const [selected, setSelected] = useState({
    ingresos: true,
    ingresos_reales: true,
    gastos: true,
    beneficio: true,
    clientes: true,
    sesiones: true,
    cancelaciones: true,
    ocupacion: true,
  });

  const getFirstName = (value) => {
    const cleanName = String(value ?? "")
      .trim()
      .replace(/\s+/g, " ");

    if (!cleanName) {
      return "Sin profesional";
    }

    return cleanName.split(" ")[0];
  };

  const isHiddenProfessional = (value) => {
    const name = normalizeText(value);
    return name === "ana" || name.startsWith("ana ");
  };

  const isPsychology = (specialty) => {
    return isPsychologyCode(specialty);
  };

  const shouldHideSpecialty = (specialty) => {
    const normalized = normalizeText(specialty)
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    const hiddenValues = [
      "administracion",
      "nutricion",
    ];

    return hiddenValues.some(
      (hiddenValue) =>
        normalized === hiddenValue ||
        normalized.includes(hiddenValue)
    );
  };

  const dataset = useMemo(() => {
    return [...(data || [])]
      .filter((row) => row && row.mes)
      .sort((a, b) =>
        String(a.mes).localeCompare(String(b.mes))
      );
  }, [data]);

  const fmt = (value, unit) => {
    const numericValue = num(value);

    if (unit === "EUR") {
      return `${numericValue.toFixed(2)} €`;
    }

    if (unit === "PCT") {
      return `${numericValue.toFixed(1)} %`;
    }

    return `${Math.round(numericValue)}`;
  };

  const metricasDinamicas = useMemo(() => {
    const specialtiesMap = new Map();

    const registerSpecialty = (specialtyItem) => {
      const originalSpecialtyName =
        String(
          specialtyItem?.especialidad ||
            specialtyItem?.nombre ||
            specialtyItem?.key ||
            "Sin especialidad"
        ).trim() || "Sin especialidad";

      if (shouldHideSpecialty(originalSpecialtyName)) {
        return;
      }

      const canonicalSpecialty =
        getCanonicalSpecialty(originalSpecialtyName);

      if (!specialtiesMap.has(canonicalSpecialty.key)) {
        specialtiesMap.set(canonicalSpecialty.key, {
          key: canonicalSpecialty.key,
          name: canonicalSpecialty.name,
          professionals: new Map(),
        });
      }

      const storedSpecialty = specialtiesMap.get(
        canonicalSpecialty.key
      );

      const professionals = Array.isArray(
        specialtyItem?.profesionales
      )
        ? specialtyItem.profesionales
        : [];

      professionals.forEach((professional) => {
        const professionalId =
          professional?.professional_id ??
          professional?.id ??
          null;

        const fullName =
          String(
            professional?.nombre ||
              professional?.name ||
              (professionalId
                ? `Profesional ${professionalId}`
                : "Sin profesional")
          ).trim() || "Sin profesional";

        if (isHiddenProfessional(fullName)) {
          return;
        }

        const professionalKey =
          professionalId !== null &&
          professionalId !== undefined
            ? String(professionalId)
            : safeKey(fullName);

        if (!storedSpecialty.professionals.has(professionalKey)) {
          storedSpecialty.professionals.set(professionalKey, {
            id: professionalId,
            key: professionalKey,
            name: getFirstName(fullName),
            fullName,
          });
        }
      });
    };

    dataset.forEach((row) => {
      const sessionDetails = Array.isArray(row?.sesiones_detalle)
        ? row.sesiones_detalle
        : [];

      const cancellationDetails = Array.isArray(
        row?.cancelaciones_detalle
      )
        ? row.cancelaciones_detalle
        : [];

      const occupationDetails = Array.isArray(
        row?.ocupacion_detalle
      )
        ? row.ocupacion_detalle
        : [];

      sessionDetails.forEach(registerSpecialty);
      cancellationDetails.forEach(registerSpecialty);
      occupationDetails.forEach(registerSpecialty);
    });

    const result = [];
    const orderedSpecialties = Array.from(
      specialtiesMap.values()
    ).sort((a, b) => {
      if (a.key === "psicologia") return -1;
      if (b.key === "psicologia") return 1;
      return a.name.localeCompare(b.name, "es", {
        sensitivity: "base",
      });
    });

    orderedSpecialties.forEach((specialty) => {
      const psychology = isPsychology(specialty.name);

      result.push({
        key: `sesiones_especialidad__${specialty.key}`,
        label: specialty.name,
        unit: "N",
        level: 1,
        type: "specialty",
        group: "sesiones",
        specialtyKey: specialty.key,
        specialtyName: specialty.name,
      });

      Array.from(specialty.professionals.values())
  .sort((a, b) =>
    a.name.localeCompare(b.name, "es", {
      sensitivity: "base",
    })
  )
  .forEach((professional) => {
    result.push({
      key:
        `sesiones_profesional__${specialty.key}` +
        `__${professional.key}`,
      label: professional.name,
      unit: "N",
      level: 2,
      type: "professional",
      group: "sesiones",
      specialtyKey: specialty.key,
      specialtyName: specialty.name,
      professionalId: professional.id,
      professionalKey: professional.key,
      professionalFullName: professional.fullName,
    });
  });

      result.push({
        key: `cancelaciones_especialidad__${specialty.key}`,
        label: specialty.name,
        unit: "N",
        level: 1,
        type: "cancellation-specialty",
        group: "cancelaciones",
        specialtyKey: specialty.key,
        specialtyName: specialty.name,
      });

      Array.from(specialty.professionals.values())
        .sort((a, b) =>
          a.name.localeCompare(b.name, "es", {
            sensitivity: "base",
          })
        )
        .forEach((professional) => {
          result.push({
            key:
              `cancelaciones_profesional__${specialty.key}` +
              `__${professional.key}`,
            label: professional.name,
            unit: "N",
            level: 2,
            type: "cancellation-professional",
            group: "cancelaciones",
            specialtyKey: specialty.key,
            specialtyName: specialty.name,
            professionalId: professional.id,
            professionalKey: professional.key,
            professionalFullName: professional.fullName,
          });
        });

      result.push({
        key: `ocupacion_especialidad__${specialty.key}`,
        label: specialty.name,
        unit: "PCT",
        level: 1,
        type: "occupation-specialty",
        group: "ocupacion",
        specialtyKey: specialty.key,
        specialtyName: specialty.name,
      });

   Array.from(specialty.professionals.values())
  .sort((a, b) =>
    a.name.localeCompare(b.name, "es", {
      sensitivity: "base",
    })
  )
  .forEach((professional) => {
    result.push({
      key:
        `ocupacion_profesional__${specialty.key}` +
        `__${professional.key}`,
      label: professional.name,
      unit: "PCT",
      level: 2,
      type: "occupation-professional",
      group: "ocupacion",
      specialtyKey: specialty.key,
      specialtyName: specialty.name,
      professionalId: professional.id,
      professionalKey: professional.key,
      professionalFullName: professional.fullName,
    });
  });
    });

    return result;
  }, [dataset]);

  const METRICAS = useMemo(() => {
    const dynamicSessions = metricasDinamicas.filter(
      (metric) => metric.group === "sesiones"
    );
    const dynamicCancellations = metricasDinamicas.filter(
      (metric) => metric.group === "cancelaciones"
    );

    const dynamicOccupation = metricasDinamicas.filter(
      (metric) => metric.group === "ocupacion"
    );

    return [
      ...BASE_METRICAS.filter(
        (metric) => metric.group === "economia"
      ),
      ...BASE_METRICAS.filter(
        (metric) => metric.group === "clientes"
      ),
      ...BASE_METRICAS.filter(
        (metric) => metric.group === "sesiones"
      ),
      ...dynamicSessions,
      ...BASE_METRICAS.filter(
        (metric) => metric.group === "cancelaciones"
      ),
      ...dynamicCancellations,
      ...BASE_METRICAS.filter(
        (metric) => metric.group === "ocupacion"
      ),
      ...dynamicOccupation,
    ];
  }, [BASE_METRICAS, metricasDinamicas]);

  useEffect(() => {
    setSelected((previous) => {
      const next = {};
      let changed = false;

      METRICAS.forEach((metric) => {
        if (
          Object.prototype.hasOwnProperty.call(
            previous,
            metric.key
          )
        ) {
          next[metric.key] = previous[metric.key];
        } else {
          next[metric.key] = true;
          changed = true;
        }
      });

      const previousKeys = Object.keys(previous);
      const nextKeys = Object.keys(next);

      if (previousKeys.length !== nextKeys.length) {
        changed = true;
      }

      return changed ? next : previous;
    });
  }, [METRICAS]);

  const findSpecialty = (row, metric) => {
    const details = Array.isArray(row?.sesiones_detalle)
      ? row.sesiones_detalle
      : [];

    return (
      details.find((item) => {
        const itemName =
          item?.especialidad ||
          item?.nombre ||
          item?.key ||
          "Sin especialidad";

        const canonicalItem =
          getCanonicalSpecialty(itemName);

        return canonicalItem.key === metric.specialtyKey;
      }) || null
    );
  };

  const findCancellationSpecialty = (row, metric) => {
    const details = Array.isArray(row?.cancelaciones_detalle)
      ? row.cancelaciones_detalle
      : [];

    return (
      details.find((item) => {
        const itemName =
          item?.especialidad ||
          item?.nombre ||
          item?.key ||
          "Sin especialidad";

        return (
          getCanonicalSpecialty(itemName).key ===
          metric.specialtyKey
        );
      }) || null
    );
  };

  const findOccupationSpecialty = (row, metric) => {
    const details = Array.isArray(row?.ocupacion_detalle)
      ? row.ocupacion_detalle
      : [];

    return (
      details.find((item) => {
        const itemName =
          item?.especialidad ||
          item?.nombre ||
          item?.key ||
          "Sin especialidad";

        return (
          getCanonicalSpecialty(itemName).key ===
          metric.specialtyKey
        );
      }) || null
    );
  };

  const getBaseMetricValue = (row, key) => {
    if (key === "ingresos") {
      return num(
        row?.ingresos ??
          row?.ingresos_facturados ??
          row?.facturado ??
          row?.total_facturado ??
          0
      );
    }

    if (key === "ingresos_reales") {
      return calcularIngresosReales(row);
    }

    if (key === "beneficio") {
      const beneficioBackend =
        row?.beneficio ??
        row?.balance ??
        row?.beneficio_real;

      if (
        beneficioBackend !== null &&
        beneficioBackend !== undefined &&
        beneficioBackend !== ""
      ) {
        return num(beneficioBackend);
      }

      return (
        calcularIngresosReales(row) -
        num(row?.gastos)
      );
    }

    if (key === "cancelaciones") {
      return num(
        row?.cancelaciones ??
          row?.cancelaciones_totales ??
          0
      );
    }

    if (key === "ocupacion") {
      return num(
        row?.ocupacion ??
          row?.tasa_ocupacion ??
          row?.porcentaje_ocupacion ??
          row?.occupation_rate ??
          0
      );
    }

    return num(row?.[key]);
  };

  const getMetricValue = (row, metricOrKey) => {
    const metric =
      typeof metricOrKey === "string"
        ? METRICAS.find(
            (item) => item.key === metricOrKey
          )
        : metricOrKey;

    if (!metric) {
      return 0;
    }

    if (metric.type === "base") {
      return getBaseMetricValue(row, metric.key);
    }

    if (
      metric.type === "cancellation-specialty" ||
      metric.type === "cancellation-professional"
    ) {
      const cancellationSpecialty =
        findCancellationSpecialty(row, metric);

      if (!cancellationSpecialty) {
        return 0;
      }

      if (metric.type === "cancellation-specialty") {
        return num(
          cancellationSpecialty?.total ??
            cancellationSpecialty?.cancelaciones ??
            0
        );
      }

      const professionals = Array.isArray(
        cancellationSpecialty?.profesionales
      )
        ? cancellationSpecialty.profesionales
        : [];

      const professional = professionals.find((item) => {
        const itemId =
          item?.professional_id ?? item?.id ?? null;

        if (
          metric.professionalId !== null &&
          metric.professionalId !== undefined
        ) {
          return (
            String(itemId ?? "") ===
            String(metric.professionalId)
          );
        }

        return (
          safeKey(item?.nombre || item?.name || "") ===
          metric.professionalKey
        );
      });

      return num(
        professional?.cancelaciones ??
          professional?.total ??
          0
      );
    }

    if (
      metric.type === "occupation-specialty" ||
      metric.type === "occupation-professional"
    ) {
      const occupationSpecialty =
        findOccupationSpecialty(row, metric);

      if (!occupationSpecialty) {
        return 0;
      }

      if (metric.type === "occupation-specialty") {
        return num(
          occupationSpecialty?.ocupacion ??
            occupationSpecialty?.tasa_ocupacion ??
            0
        );
      }

      const professionals = Array.isArray(
        occupationSpecialty?.profesionales
      )
        ? occupationSpecialty.profesionales
        : [];

      const professional = professionals.find((item) => {
        const itemId =
          item?.professional_id ?? item?.id ?? null;

        if (
          metric.professionalId !== null &&
          metric.professionalId !== undefined
        ) {
          return (
            String(itemId ?? "") ===
            String(metric.professionalId)
          );
        }

        return (
          safeKey(item?.nombre || item?.name || "") ===
          metric.professionalKey
        );
      });

      return num(
        professional?.ocupacion ??
          professional?.tasa_ocupacion ??
          0
      );
    }

    const specialty = findSpecialty(row, metric);

    if (!specialty) {
      return 0;
    }

    if (metric.type === "specialty") {
      return num(
        specialty?.total ??
          specialty?.sesiones ??
          0
      );
    }

    if (metric.type === "professional") {
      const professionals = Array.isArray(
        specialty?.profesionales
      )
        ? specialty.profesionales
        : [];

      const professional = professionals.find(
        (item) => {
          const itemId =
            item?.professional_id ??
            item?.id ??
            null;

          if (
            metric.professionalId !== null &&
            metric.professionalId !== undefined
          ) {
            return (
              String(itemId ?? "") ===
              String(metric.professionalId)
            );
          }

          const itemName =
            item?.nombre ||
            item?.name ||
            "";

          return (
            safeKey(itemName) ===
            metric.professionalKey
          );
        }
      );

      return num(
        professional?.sesiones ??
          professional?.total ??
          0
      );
    }

    return 0;
  };

  const selectedKeys = useMemo(() => {
    return METRICAS.map(
      (metric) => metric.key
    ).filter((key) => Boolean(selected[key]));
  }, [METRICAS, selected]);

  const selectedMetrics = useMemo(() => {
    return METRICAS.filter((metric) =>
      selectedKeys.includes(metric.key)
    );
  }, [METRICAS, selectedKeys]);

  const toggle = (key) => {
    setSelected((previous) => ({
      ...previous,
      [key]: !previous[key],
    }));
  };

  const mesesLabels = useMemo(() => {
    const months = [
      "Ene",
      "Feb",
      "Mar",
      "Abr",
      "May",
      "Jun",
      "Jul",
      "Ago",
      "Sep",
      "Oct",
      "Nov",
      "Dic",
    ];

    const lastDatasetYear = dataset.length
      ? Number(
          String(
            dataset[dataset.length - 1].mes
          ).slice(0, 4)
        )
      : new Date().getFullYear();

    const mainYear =
      Number(principalYear) || lastDatasetYear;

    return dataset.map((row) => {
      if (row.label) {
        return String(row.label);
      }

      const [year, month] =
        String(row.mes).split("-");

      const monthIndex = Math.max(
        0,
        Math.min(11, Number(month) - 1)
      );

      const baseLabel = months[monthIndex];

      if (Number(year) !== mainYear) {
        return `${baseLabel} ${String(year).slice(-2)}`;
      }

      return baseLabel;
    });
  }, [dataset, principalYear]);

  const calcStats = (metric) => {
    const values = dataset.map((row) =>
      getMetricValue(row, metric)
    );

    const valuesCount = values.length;

    const sum = values.reduce(
      (accumulator, value) =>
        accumulator + value,
      0
    );

    const average = valuesCount
      ? sum / valuesCount
      : 0;

    const first = valuesCount
      ? values[0]
      : 0;

    const last = valuesCount
      ? values[valuesCount - 1]
      : 0;

    const delta = last - first;

    const deltaPct =
      first !== 0
        ? (delta / first) * 100
        : null;

    return {
      avg: average,
      first,
      last,
      delta,
      deltaPct,
    };
  };

  const selectedByGroup = useMemo(() => {
    const result = {
      economia: [],
      actividad: [],
      cancelaciones: [],
      ocupacion: [],
    };

    selectedMetrics.forEach((metric) => {
      if (metric.group === "economia") {
        result.economia.push(metric);
        return;
      }

      if (
        metric.group === "clientes" ||
        metric.group === "sesiones"
      ) {
        result.actividad.push(metric);
        return;
      }

      if (metric.group === "cancelaciones") {
        result.cancelaciones.push(metric);
        return;
      }

      if (metric.group === "ocupacion") {
        result.ocupacion.push(metric);
      }
    });

    return result;
  }, [selectedMetrics]);

  const GROUP_LABELS = {
    economia: "Economía",
    clientes: "Actividad",
    sesiones: "Sesiones",
    cancelaciones: "Cancelaciones",
    ocupacion: "Ocupación",
  };

  const groupedMetrics = useMemo(() => {
    return [
      "economia",
      "clientes",
      "sesiones",
      "cancelaciones",
      "ocupacion",
    ]
      .map((groupKey) => ({
        key: groupKey,
        label: GROUP_LABELS[groupKey],
        metrics: METRICAS.filter(
          (metric) => metric.group === groupKey
        ),
      }))
      .filter((group) => group.metrics.length > 0);
  }, [METRICAS]);

  const renderMetricLabel = (metric) => {
    return metric.label;
  };

  const BarsChart = ({
    metrics,
    unit,
    title,
  }) => {
    const width = 980;
    const height = 320;

    const padLeft = 52;
    const padRight = 16;
    const padTop = 16;
    const padBottom = 74;

    const numberOfMonths = dataset.length;

    const allValues = dataset.flatMap((row) =>
      metrics.map((metric) =>
        getMetricValue(row, metric)
      )
    );

    const calculatedMaximum = Math.max(
      1,
      ...(allValues.length
        ? allValues
        : [1])
    );

    const maxValue =
      unit === "PCT"
        ? Math.max(100, calculatedMaximum)
        : calculatedMaximum;

    const innerWidth =
      width - padLeft - padRight;

    const innerHeight =
      height - padTop - padBottom;

    const groupWidth =
      numberOfMonths > 0
        ? innerWidth / numberOfMonths
        : innerWidth;

    const groupGap = Math.max(
      6,
      groupWidth * 0.12
    );

    const usableGroupWidth = Math.max(
      1,
      groupWidth - groupGap
    );

    const barsPerGroup = Math.max(
      1,
      metrics.length
    );

    const barGap = Math.max(
      2,
      usableGroupWidth * 0.04
    );

    const barWidth = Math.max(
      3,
      (usableGroupWidth -
        barGap * (barsPerGroup - 1)) /
        barsPerGroup
    );

    const groupX = (monthIndex) => {
      return (
        padLeft +
        monthIndex * groupWidth +
        groupGap / 2
      );
    };

    const barX = (
      monthIndex,
      seriesIndex
    ) => {
      return (
        groupX(monthIndex) +
        seriesIndex *
          (barWidth + barGap)
      );
    };

    const y = (value) => {
      const position =
        num(value) / (maxValue || 1);

      return (
        padTop +
        (1 - position) * innerHeight
      );
    };

    const barHeight = (value) => {
      return (
        height -
        padBottom -
        y(value)
      );
    };

    const gridLinesCount = 4;

    const grid = Array.from(
      {
        length: gridLinesCount + 1,
      },
      (_, index) =>
        padTop +
        (index * innerHeight) /
          gridLinesCount
    );

    const palette = [
      "#3b82f6",
      "#ef4444",
      "#10b981",
      "#a855f7",
      "#f59e0b",
      "#06b6d4",
      "#ec4899",
      "#84cc16",
      "#6366f1",
      "#f97316",
    ];

    const tick = (index) => {
      const value =
        maxValue *
        (1 - index / gridLinesCount);

      if (unit === "EUR") {
        return `${value.toFixed(0)}€`;
      }

      if (unit === "PCT") {
        return `${value.toFixed(0)}%`;
      }

      return `${Math.round(value)}`;
    };

    return (
      <div className="ev-chart">
        <div className="ev-chart-title">
          {title}
        </div>

        <svg
          viewBox={`0 0 ${width} ${height}`}
          width="100%"
          height="320"
          role="img"
        >
          {grid.map((gridY, index) => (
            <g key={index}>
              <line
                x1={padLeft}
                x2={width - padRight}
                y1={gridY}
                y2={gridY}
                stroke="#e5e7eb"
                strokeWidth="1"
              />

              <text
                x={padLeft - 8}
                y={gridY + 4}
                textAnchor="end"
                fontSize="11"
                fill="#6b7280"
              >
                {tick(index)}
              </text>
            </g>
          ))}

          <line
            x1={padLeft}
            x2={width - padRight}
            y1={height - padBottom}
            y2={height - padBottom}
            stroke="#9ca3af"
            strokeWidth="1"
          />

          {dataset.map(
            (row, monthIndex) => (
              <g key={row.mes}>
                {metrics.map(
                  (
                    metric,
                    seriesIndex
                  ) => {
                    const value =
                      getMetricValue(
                        row,
                        metric
                      );

                    if (!value) {
                      return null;
                    }

                    const color =
                      palette[
                        seriesIndex %
                          palette.length
                      ];

                    const rectangleX =
                      barX(
                        monthIndex,
                        seriesIndex
                      );

                    const rectangleY =
                      y(value);

                    const rectangleHeight =
                      barHeight(value);

                    return (
                      <rect
                        key={metric.key}
                        x={rectangleX}
                        y={rectangleY}
                        width={barWidth}
                        height={
                          rectangleHeight
                        }
                        fill={color}
                        rx="4"
                      >
                        <title>
                          {`${metric.label}: ${fmt(
                            value,
                            metric.unit
                          )}`}
                        </title>
                      </rect>
                    );
                  }
                )}

                <text
                  x={
                    groupX(monthIndex) +
                    usableGroupWidth / 2
                  }
                  y={height - 18}
                  textAnchor="end"
                  fontSize="12"
                  fill="#374151"
                  transform={
                    `rotate(-45 ` +
                    `${
                      groupX(
                        monthIndex
                      ) +
                      usableGroupWidth /
                        2
                    } ` +
                    `${height - 18})`
                  }
                >
                  {
                    mesesLabels[
                      monthIndex
                    ]
                  }
                </text>
              </g>
            )
          )}
        </svg>

        <div className="ev-legend">
          {metrics.map(
            (metric, index) => (
              <div
                key={metric.key}
                className="ev-legend-item"
              >
                <span
                  className="ev-dot"
                  style={{
                    background:
                      palette[
                        index %
                          palette.length
                      ],
                  }}
                />

                {renderMetricLabel(metric)}
              </div>
            )
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="ev-wrap">
      <div className="ev-top">
        <div>
          <h3 className="ev-title">
            Comparativa mes a mes
          </h3>

          <div className="ev-sub">
            Marca las métricas para comparar su
            evolución mensual.
          </div>
        </div>
      </div>

      <div className="ev-filters">
        {groupedMetrics.map((group) => (
          <div
            key={group.key}
            style={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "8px 14px",
              width: "100%",
              padding: "6px 0",
            }}
          >
            <strong style={{ minWidth: "145px" }}>
              {group.label}:
            </strong>

            {group.metrics.map((metric) => (
              <label
                key={metric.key}
                className={`ev-chk ev-chk-level-${metric.level}`}
                style={{
                  marginLeft:
                    metric.level === 2 ? "12px" : "0",
                }}
              >
                <input
                  type="checkbox"
                  checked={Boolean(selected[metric.key])}
                  onChange={() => toggle(metric.key)}
                />

                {renderMetricLabel(metric)}
              </label>
            ))}
          </div>
        ))}
      </div>

      {dataset.length === 0 ? (
        <div className="ev-empty">
          No hay datos.
        </div>
      ) : selectedKeys.length === 0 ? (
        <div className="ev-empty">
          Marca al menos una métrica para ver la
          comparativa.
        </div>
      ) : (
        <>
          {selectedByGroup.economia.length > 0 ? (
            <BarsChart
              metrics={selectedByGroup.economia}
              unit="EUR"
              title="Economía"
            />
          ) : null}

          {selectedByGroup.actividad.length > 0 ? (
            <BarsChart
              metrics={selectedByGroup.actividad}
              unit="N"
              title="Actividad"
            />
          ) : null}

          {selectedByGroup.cancelaciones.length > 0 ? (
            <BarsChart
              metrics={selectedByGroup.cancelaciones}
              unit="N"
              title="Cancelaciones"
            />
          ) : null}

          {selectedByGroup.ocupacion.length > 0 ? (
            <BarsChart
              metrics={selectedByGroup.ocupacion}
              unit="PCT"
              title="Ocupación"
            />
          ) : null}

          <div className="ev-table-wrap">
            <table className="ev-table">
              <thead>
                <tr>
                  <th>Métrica</th>

                  {mesesLabels.map(
                    (monthLabel, index) => (
                      <th key={index}>
                        {monthLabel}
                      </th>
                    )
                  )}

                  <th>Media</th>
                  <th>Δ (1º→último)</th>
                </tr>
              </thead>

              <tbody>
                {groupedMetrics.map((group) => {
                  const visibleMetrics = group.metrics.filter(
                    (metric) => selected[metric.key]
                  );

                  if (visibleMetrics.length === 0) {
                    return null;
                  }

                  return (
                    <React.Fragment key={group.key}>
                      <tr>
                        <td
                          colSpan={dataset.length + 3}
                          style={{
                            fontWeight: 700,
                            background: "rgba(255,255,255,0.45)",
                            paddingTop: "10px",
                            paddingBottom: "10px",
                          }}
                        >
                          {group.label}
                        </td>
                      </tr>

                      {visibleMetrics.map((metric) => {
                        const stats = calcStats(metric);

                        return (
                          <tr key={metric.key}>
                            <td
                              className={`ev-metric ev-metric-level-${metric.level}`}
                            >
                              {renderMetricLabel(metric)}
                            </td>

                            {dataset.map((row, index) => (
                              <td key={index}>
                                {fmt(
                                  getMetricValue(row, metric),
                                  metric.unit
                                )}
                              </td>
                            ))}

                            <td>{fmt(stats.avg, metric.unit)}</td>

                            <td>
                              {fmt(stats.delta, metric.unit)}

                              {stats.deltaPct !== null ? (
                                <span className="ev-deltaPct">
                                  {" "}({stats.deltaPct.toFixed(1)}%)
                                </span>
                              ) : null}
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}